import React, { useState, useEffect } from "react";
import {
  BarChart3,
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Award,
  Download,
  PieChart,
  Activity,
} from "lucide-react";
import { calcularDiasRestantes, formatarData } from "../utils/dateUtils";
import { useCronograma } from "../hooks/useCronograma";
import { useAuth } from "../context/AuthContext";
import { useFlowContext } from "../hooks/useFlowContext";
import { getSistemaInfo } from "../utils/sistemaInfo";

const RelatoriosTab: React.FC = () => {
  const { fases, loading, error, getEstatisticas } = useCronograma();
  const { empresaSelecionada } = useAuth();
  const { context } = useFlowContext();

  // Verificar se pode exportar baseado no plano
  const planoUsuario = context.planoUsuario;
  const podeExportar = ["PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"].includes(
    planoUsuario,
  );

  // Informações dinâmicas do sistema
  const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
  const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
  const nomeEmpresa = (empresaSelecionada as any)?.nome;

  const [sistemaInfo, setSistemaInfo] = useState({
    norma: "NR-05",
    tipo: "CIPA" as "CIPA" | "CIPAMIN" | "CIPATR",
  });

  useEffect(() => {
    getSistemaInfo(cnaeEmpresa, tipoComissaoBanco, nomeEmpresa).then(
      setSistemaInfo,
    );
  }, [cnaeEmpresa, tipoComissaoBanco, nomeEmpresa]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <p className="text-red-700">Erro ao carregar dados: {error}</p>
      </div>
    );
  }

  const stats = getEstatisticas();
  const totalFases = fases.length;
  const fasesVencidas = fases.filter(
    (fase) => calcularDiasRestantes(fase.data) < 0,
  );
  const fasesProximasVencimento = fases.filter((fase) => {
    const diasRestantes = calcularDiasRestantes(fase.data);
    return diasRestantes > 0 && diasRestantes <= 7;
  });
  const fasesNoPrazo = fases.filter((fase) => {
    const diasRestantes = calcularDiasRestantes(fase.data);
    return diasRestantes > 7 && diasRestantes <= 30;
  });
  const fasesConforme = fases.filter((fase) => {
    const diasRestantes = calcularDiasRestantes(fase.data);
    return diasRestantes > 30;
  });

  // Calcular progresso geral
  const progressoGeral = Math.round((stats.concluidas / totalFases) * 100);

  // Calcular conformidade com a norma vigente
  const conformidadeNorma = Math.round(
    ((totalFases - fasesVencidas.length) / totalFases) * 100,
  );

  // Função para exportar dados em CSV
  const exportarDados = () => {
    // Cabeçalho do CSV
    const headers = [
      "Fase",
      "Data",
      "Status",
      "Dias Restantes",
      "Progresso (%)",
      "Ações Concluídas",
      "Total de Ações",
      `Artigo ${sistemaInfo.norma}`,
    ];

    // Dados das fases
    const rows = fases.map((fase) => {
      const diasRestantes = calcularDiasRestantes(fase.data);
      const progresso = fase.acoes_banco
        ? Math.round(
            (fase.acoes_banco.filter((acao) => acao.concluida).length /
              fase.acoes_banco.length) *
              100,
          )
        : 0;
      const acoesConcluidas = fase.acoes_banco
        ? fase.acoes_banco.filter((acao) => acao.concluida).length
        : 0;
      const totalAcoes = fase.acoes_banco ? fase.acoes_banco.length : 0;

      return [
        fase.titulo,
        formatarData(fase.data),
        diasRestantes < 0
          ? "Vencida"
          : diasRestantes <= 7
            ? "Urgente"
            : "No prazo",
        diasRestantes,
        progresso,
        acoesConcluidas,
        totalAcoes,
        fase.artigo,
      ];
    });

    // Adicionar linha de estatísticas gerais
    const estatisticasRow = [
      "ESTATÍSTICAS GERAIS",
      "",
      "",
      "",
      `${progressoGeral}%`,
      `${stats.concluidas}`,
      `${totalFases}`,
      `Conformidade: ${conformidadeNorma}%`,
    ];

    // Combinar cabeçalho e dados
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      "",
      estatisticasRow.map((cell) => `"${cell}"`).join(","),
    ].join("\n");

    // Criar e baixar arquivo CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `relatorio-cipa-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header do Relatório */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Relatórios {sistemaInfo.tipo}
            </h1>
            <p className="text-blue-100">
              Análise Completa do Processo Eleitoral - {sistemaInfo.norma}
            </p>
          </div>
          <div className="flex gap-3">
            {podeExportar && (
              <button
                onClick={exportarDados}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Exportar CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mensagem de upgrade para STARTER */}
      {!podeExportar && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Download className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">
                💎 Exportação de Relatórios
              </p>
              <p className="text-sm text-blue-700">
                A exportação de relatórios em CSV está disponível no plano
                PROFESSIONAL ou superior. Atualize seu plano para ter acesso
                completo aos recursos de exportação.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Estatísticas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Progresso Geral</p>
              <p className="text-2xl font-bold text-green-600">
                {progressoGeral}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                Conformidade {sistemaInfo.norma}
              </p>
              <p className="text-2xl font-bold text-blue-600">
                {conformidadeNorma}%
              </p>
            </div>
            <Award className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Próximo Vencimento</p>
              <p className="text-2xl font-bold text-orange-600">
                {stats.proximoVencimento}
              </p>
            </div>
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Fases Vencidas</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.vencidas}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Gráfico de Progresso */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          Progresso por Fase
        </h3>
        <div className="space-y-4">
          {fases.map((fase) => {
            const diasRestantes = calcularDiasRestantes(fase.data);
            const progresso = fase.acoes_banco
              ? Math.round(
                  (fase.acoes_banco.filter((acao) => acao.concluida).length /
                    fase.acoes_banco.length) *
                    100,
                )
              : 0;

            return (
              <div key={fase.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`${fase.cor} p-2 rounded-lg`}>
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">
                        {fase.titulo}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {formatarData(fase.data)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        diasRestantes < 0
                          ? "bg-red-100 text-red-800"
                          : diasRestantes <= 7
                            ? "bg-orange-100 text-orange-800"
                            : "bg-green-100 text-green-800"
                      }`}
                    >
                      {diasRestantes < 0 ? "Vencida" : `${diasRestantes} dias`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progresso</span>
                      <span>{progresso}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${progresso}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {fase.acoes_banco
                      ? `${fase.acoes_banco.filter((acao) => acao.concluida).length}/${fase.acoes_banco.length} ações`
                      : "0/0 ações"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Análise de Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fases Vencidas */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Fases Vencidas ({fasesVencidas.length})
          </h3>
          {fasesVencidas.length > 0 ? (
            <div className="space-y-3">
              {fasesVencidas.map((fase) => (
                <div
                  key={fase.id}
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200"
                >
                  <div>
                    <p className="font-medium text-red-800">{fase.titulo}</p>
                    <p className="text-sm text-red-600">
                      Vencida em {formatarData(fase.data)}
                    </p>
                  </div>
                  <span className="text-red-600 font-bold">
                    {Math.abs(calcularDiasRestantes(fase.data))} dias atrasada
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-600 font-medium">
                Nenhuma fase vencida!
              </p>
              <p className="text-sm text-gray-600">
                Todas as fases estão no prazo
              </p>
            </div>
          )}
        </div>

        {/* Próximas ao Vencimento */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Próximas ao Vencimento ({fasesProximasVencimento.length})
          </h3>
          {fasesProximasVencimento.length > 0 ? (
            <div className="space-y-3">
              {fasesProximasVencimento.map((fase) => (
                <div
                  key={fase.id}
                  className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200"
                >
                  <div>
                    <p className="font-medium text-orange-800">{fase.titulo}</p>
                    <p className="text-sm text-orange-600">
                      Vence em {formatarData(fase.data)}
                    </p>
                  </div>
                  <span className="text-orange-600 font-bold">
                    {calcularDiasRestantes(fase.data)} dias
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-600 font-medium">
                Nenhuma fase próxima ao vencimento!
              </p>
              <p className="text-sm text-gray-600">
                Todas as fases têm prazo adequado
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Distribuição por Status */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <PieChart className="w-5 h-5 text-purple-600" />
          Distribuição por Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600 mb-1">
              {fasesVencidas.length}
            </div>
            <div className="text-sm text-red-700">Vencidas</div>
            <div className="text-xs text-red-500">
              {Math.round((fasesVencidas.length / totalFases) * 100)}%
            </div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {fasesProximasVencimento.length}
            </div>
            <div className="text-sm text-orange-700">Próximas</div>
            <div className="text-xs text-orange-500">
              {Math.round((fasesProximasVencimento.length / totalFases) * 100)}%
            </div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600 mb-1">
              {fasesNoPrazo.length}
            </div>
            <div className="text-sm text-yellow-700">No Prazo</div>
            <div className="text-xs text-yellow-500">
              {Math.round((fasesNoPrazo.length / totalFases) * 100)}%
            </div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {fasesConforme.length}
            </div>
            <div className="text-sm text-green-700">Conforme</div>
            <div className="text-xs text-green-500">
              {Math.round((fasesConforme.length / totalFases) * 100)}%
            </div>
          </div>
        </div>
      </div>

      {/* Cronograma Detalhado */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-indigo-600" />
          Cronograma Detalhado
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700">
                  Fase
                </th>
                <th className="text-left p-3 font-semibold text-gray-700">
                  Data
                </th>
                <th className="text-left p-3 font-semibold text-gray-700">
                  Status
                </th>
                <th className="text-left p-3 font-semibold text-gray-700">
                  Progresso
                </th>
                <th className="text-left p-3 font-semibold text-gray-700">
                  Artigo {sistemaInfo.norma}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fases.map((fase) => {
                const diasRestantes = calcularDiasRestantes(fase.data);
                const progresso = fase.acoes_banco
                  ? Math.round(
                      (fase.acoes_banco.filter((acao) => acao.concluida)
                        .length /
                        fase.acoes_banco.length) *
                        100,
                    )
                  : 0;

                return (
                  <tr key={fase.id} className="hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className={`${fase.cor} p-1 rounded`}>
                          <Calendar className="w-3 h-3 text-white" />
                        </div>
                        <span className="font-medium">{fase.titulo}</span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {formatarData(fase.data)}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          diasRestantes < 0
                            ? "bg-red-100 text-red-800"
                            : diasRestantes <= 7
                              ? "bg-orange-100 text-orange-800"
                              : "bg-green-100 text-green-800"
                        }`}
                      >
                        {diasRestantes < 0
                          ? "Vencida"
                          : diasRestantes <= 7
                            ? "Urgente"
                            : "No prazo"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${progresso}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">
                          {progresso}%
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-blue-600">{fase.artigo}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumo Executivo */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg p-6">
        <h3 className="text-xl font-bold mb-4">Resumo Executivo</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-indigo-200 text-sm">Status Geral</p>
            <p className="text-2xl font-bold">
              {conformidadeNorma >= 90
                ? "Excelente"
                : conformidadeNorma >= 70
                  ? "Bom"
                  : conformidadeNorma >= 50
                    ? "Regular"
                    : "Crítico"}
            </p>
          </div>
          <div>
            <p className="text-indigo-200 text-sm">
              Conformidade {sistemaInfo.norma}
            </p>
            <p className="text-2xl font-bold">{conformidadeNorma}%</p>
          </div>
          <div>
            <p className="text-indigo-200 text-sm">Próxima Ação Crítica</p>
            <p className="text-lg font-medium">
              {fasesProximasVencimento.length > 0
                ? fasesProximasVencimento[0].titulo
                : "Nenhuma ação crítica pendente"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatoriosTab;
